import type { JobStep, StepStatus } from "../api/types";
import { useJobProgress } from "../hooks/useJobProgress";
import { Alert, Card } from "./ui";

const ICONS: Record<StepStatus, string> = {
  pending: "○",
  running: "◍",
  done: "●",
  skipped: "◌",
  failed: "✕",
};

const TONES: Record<StepStatus, string> = {
  pending: "text-gray-400",
  running: "text-accent-600",
  done: "text-accent-600",
  skipped: "text-gray-400",
  failed: "text-red-600",
};

/**
 * Live generation progress.
 *
 * Replaces the terminal output generate.py printed. `onFinished` lets the page
 * refetch once the chain is done, so the documents appear without a reload.
 */
export default function JobProgress({
  jobId,
  onFinished,
}: {
  jobId: string;
  onFinished: () => void;
}) {
  const { job, streaming, error, finished } = useJobProgress(jobId);

  if (finished) {
    // Defer, so the parent is not updated during this render.
    queueMicrotask(onFinished);
  }

  if (!job) return null;

  return (
    <Card title="Generating">
      <div className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}

        <ol className="space-y-1.5">
          {job.steps.map((step) => (
            <StepRow key={step.name} step={step} />
          ))}
        </ol>

        {streaming && (
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500">Writing…</p>
            <pre className="max-h-48 overflow-y-auto rounded-md bg-gray-50 p-3 text-xs whitespace-pre-wrap text-gray-700">
              {streaming.text.slice(-1500)}
            </pre>
          </div>
        )}

        <p className="text-xs text-gray-500">
          This takes a few minutes. You can leave the page — it keeps running, and the
          progress picks up where it left off when you come back.
        </p>
      </div>
    </Card>
  );
}

function StepRow({ step }: { step: JobStep }) {
  return (
    <li className="flex items-baseline gap-3 text-sm">
      <span
        aria-hidden
        className={`${TONES[step.status]} ${step.status === "running" ? "animate-pulse" : ""}`}
      >
        {ICONS[step.status]}
      </span>
      <span className={step.status === "pending" ? "text-gray-400" : "text-gray-800"}>
        {step.label}
      </span>
      {step.status === "skipped" && (
        <span className="text-xs text-gray-400">already up to date</span>
      )}
      {step.error && <span className="text-xs text-red-600">{step.error}</span>}
    </li>
  );
}
